import React, { useState } from 'react'
import Markdown from 'marked-react'
import { IconBulb } from '@tabler/icons-react'

import { Accordion } from '@/components/accordions/Accordion'
import { Banner } from '@/components/banners/Banner'
import { Pill } from '@/components/pills/Pill'
import { Table } from '@/components/tables/Table/Table'
import { markdownLinkRenderer } from '@/utils/helpers'

// Markdown tables eat vertical space once they have more than a few rows, so render the
// large ones collapsed behind the shared Accordion; small comparison tables stay open.
const COLLAPSE_ABOVE_ROWS = 5

const CollapsibleMarkdownTable = ({ accordionId, rowCount, children }) => {
  const [expanded, setExpanded] = useState(rowCount <= COLLAPSE_ABOVE_ROWS)
  const rowLabel = rowCount ? ` · ${rowCount} ${rowCount === 1 ? 'row' : 'rows'}` : ''
  return (
    <Accordion
      styled
      id={accordionId}
      title={`Table${rowLabel}`}
      // A chevron alone reads as decoration on the white transcript; naming the
      // action is what tells the reader the header opens.
      actions={
        <Pill brand small>
          {expanded ? 'Hide' : 'Show'}
        </Pill>
      }
      expanded={expanded}
      handleToggle={() => setExpanded((prev) => !prev)}
    >
      {/* `plain` because the accordion is already the card — the default balto-table
          draws its own border, radius, and filled header, which nests a second card
          inside the first. Striping carries the row separation `plain` drops. */}
      <Table plain compact>
        {children}
      </Table>
    </Accordion>
  )
}

// Shared renderer for all model-generated markdown in Kodi: strips inline images
// (alt text only) and collapses large tables, so untrusted content can't overflow
// a message or the interactive panel. Used by KodiPage and KodiInteractivePrompt.
export const kodiMarkdownRenderer = {
  ...markdownLinkRenderer,
  image(src, alt) {
    return alt || ''
  },
  table(children) {
    // marked-react increments elementId before calling the renderer, so it's a stable,
    // unique key for this table within the parse (mirrors the default renderer).
    const key = `kodi-table-${this.elementId}`
    // children is [tableHeader, tableBody]; the body's children are the <tr> rows.
    const body = Array.isArray(children) ? children[1] : null
    const rowCount = body?.props?.children?.length ?? 0
    return (
      <CollapsibleMarkdownTable key={key} accordionId={key} rowCount={rowCount}>
        {children}
      </CollapsibleMarkdownTable>
    )
  },
}

// Kodi's system prompt (Rule 5) writes this heading; a model may decorate it (`:`, bold, an
// emoji, closing hashes). The section runs to the next heading or the end of the message.
const KNOWLEDGE_GAP_TITLE = 'Knowledge gap identified'
const KNOWLEDGE_GAP_HEADING = new RegExp(
  `^#{2,4}[^\\w\\n#]*${KNOWLEDGE_GAP_TITLE}[^\\w\\n]*$`,
  'im'
)

export const splitKnowledgeGap = (text) => {
  const heading = KNOWLEDGE_GAP_HEADING.exec(text)
  if (!heading) {
    return null
  }
  const bodyStart = heading.index + heading[0].length
  const nextHeading = text.slice(bodyStart).search(/^#{1,4}\s/m)
  const end = nextHeading === -1 ? text.length : bodyStart + nextHeading
  return {
    before: text.slice(0, heading.index),
    body: text.slice(bodyStart, end).trim(),
    after: text.slice(end),
  }
}

export const KodiMarkdown = ({ children }) => {
  const gap = splitKnowledgeGap(children)
  if (!gap) {
    return <Markdown renderer={kodiMarkdownRenderer}>{children}</Markdown>
  }
  return (
    <>
      {gap.before.trim() && <Markdown renderer={kodiMarkdownRenderer}>{gap.before}</Markdown>}
      <Banner
        magical
        icon={<IconBulb />}
        header={KNOWLEDGE_GAP_TITLE}
        dataTestId="kodi-knowledge-gap"
      >
        <Markdown renderer={kodiMarkdownRenderer}>{gap.body}</Markdown>
      </Banner>
      {gap.after.trim() && <Markdown renderer={kodiMarkdownRenderer}>{gap.after}</Markdown>}
    </>
  )
}
